import {useCallback, useRef} from "react";
import {useStreamConnector} from "./useStreamConnector";
import {getStreamURL} from "../queries/search";
import {safeParseJSON} from "../lib/utils";

/**
 * SSE event types emitted by the search stream
 */
const SEARCH_SSE_EVENT_TYPES = [
  "search_update",    // Progress updates during search
  "search_results",   // Final results with first batch
  "search_error",     // Search error occurred
  "error",            // What the server sends for a failed search, or a stream it gave up on
  "ping",             // Keep-alive ping
];

/**
 * Hook to handle streaming for search results.
 * Uses useStreamConnector which supports both SSE and JSON polling fallback.
 *
 * @param {string} streamId - The stream ID from the initial search request
 * @param {Object} auth - Auth object with jwtToken
 * @param {Object} callbacks - Callbacks for different events
 * @param {Function} callbacks.onProgress - Called with (progress, total, info) on search_update
 * @param {Function} callbacks.onResults - Called with parsed results on search_results (final batch)
 * @param {Function} callbacks.onError - Called with error on search_error, error, or connection error
 * @returns {Object} - { streamURL, isConnected, pollingMode }
 */
export const useSearchStream = (streamId, auth, callbacks = {}) => {
  const {
    onProgress = null,
    onResults = null,
    onError = null,
  } = callbacks;

  const onProgressRef = useRef(onProgress);
  const onResultsRef = useRef(onResults);
  const onErrorRef = useRef(onError);
  const isConnectedRef = useRef(false);

  const handleEvent = useCallback((event) => {
    isConnectedRef.current = true;
    const eventType = event.type;
    const data = safeParseJSON(event.data);

    if (!data) {
      // "stream_end" is a non-JSON sentinel emitted by the backend after the final event.
      // It signals MCP clients; the browser client can safely ignore it.
      if (event.data !== "stream_end") {
        console.warn("Failed to parse SSE data:", event.data);
      }
      return;
    }

    switch (eventType) {
      case "search_update":
        if (onProgressRef.current) {
          const {progress = 0, total = 0, info = ""} = data;
          onProgressRef.current(progress, total, info);
        }
        break;
      case "search_results":
        if (onResultsRef.current) {
          onResultsRef.current(data);
        }
        break;
      case "search_error":
      // The server sends "error", not "search_error": set_search_status for a failed
      // search, and the zombie-stream detector when it closes the stream. Without this
      // case the page waited for results that would never come (PLEX-6630).
      case "error":
        if (onErrorRef.current) {
          const error = new Error(data.error || "Search failed");
          error.data = data;
          onErrorRef.current(error);
        }
        break;
      case "ping":
      case "heartbeat":
        break;
      default:
        console.debug("Received SSE message:", eventType, data);
    }
  }, []);

  const handleError = useCallback((event) => {
    console.debug("Search stream connection error:", event);
    isConnectedRef.current = false;
    if (onErrorRef.current) {
      const error = new Error(event.reason || "Server connection error");
      error.details = event;
      onErrorRef.current(error);
    }
  }, []);

  const { pollingMode } = useStreamConnector({
    streamId,
    auth,
    onEvent: handleEvent,
    onError: handleError,
  });

  return {
    streamURL: streamId ? getStreamURL(streamId) : null,
    isConnected: isConnectedRef.current,
    pollingMode,
  };
};